import React, {useState, useEffect} from 'react'
import styled from 'styled-components'
import {getRecurrenceLists, getWeekDay} from '../../../utils/recurrence'
import ListSelect from '../../../components/ListSelect/ListSelect'
import CalendarField from '../../../components/Calendar/CalendarField/CalendarField'
import Icons from '../../../components/Icons'
import WeekDaySelect from '../../../components/WeekDaySelect/WeekDaySelect'
import ToggleButton from '../../../components/ToggleButton/ToggleButton'
import AmenitySelect from '../../../components/AmenitySelect/AmenitySelect'
import {_L, useLocaleState} from '../../../context/LocaleContext'
import InputRange from '../../../components/InputRange/InputRange'
import {
  convertTimeStringToNumber,
  convertTimeNumberToString,
  formatTimeWithMeridiem
} from '../../../utils/time'
import {
  useRecurrenceState,
  useRecurrenceDispatch,
  setRecurrenceValue
} from '../../../context/RecurrenceContext'
import {useUIState} from '../../../context/UIContext'
import {fetchAmenitiesForFloor} from '../../../api/room'
import {
  useMeetingDispatch,
  useMeetingState
} from '../../../context/MeetingContext'
import {
  useConfigState,
  getReservationTimes
} from '../../../context/ConfigContext'
import Spinner from '../../../components/Spinner/Spinner'
import {getReservationStartEndTimes} from '../../../utils/meeting'

const MeetingRoomFilter = ({
  onSearch,
  showClose,
  onCloseFilter,
  capacityByFloor,
  bookingRangeByFloor,
  isLoading
}) => {
  const localeState = useLocaleState()
  const [toggleTime, setToggleTime] = useState(false)
  // const [isLoading, setIsLoading] = useState(false)
  const recurrence = useRecurrenceState()
  const recurrenceDispatch = useRecurrenceDispatch()

  const uiState = useUIState()
  const {activeFloor} = uiState
  const recurrenceLists = getRecurrenceLists({localeState})
  const [roomResources, setRoomResources] = useState([])

  const meetingDispatch = useMeetingDispatch()
  const {search} = useMeetingState()

  const setMeetingSearch = payload =>
    meetingDispatch({type: 'setMeetingSearch', payload})

  useEffect(
    () => {
      let isMounted = true
      const {blId, flId} = activeFloor
      if (blId) {
        fetchAmenitiesForFloor({blId, flId}).then(amenities => {
          if (isMounted) {
            setRoomResources(amenities)
          }
        })
      }

      return () => {
        isMounted = false
      }
    },
    [activeFloor]
  )

  const onMeetingTimeClick = idx => setToggleTime(idx)

  const setMeetingTime = ({min, max}) => {
    setMeetingSearch({
      time_start: convertTimeNumberToString({timeNumber: min}),
      time_end: convertTimeNumberToString({timeNumber: max})
    })
  }

  const setCapacity = capacity => setMeetingSearch({capacity})

  const onSelectResources = resources =>
    setMeetingSearch({resources: resources})

  const onSelectDays = day =>
    setRecurrenceValue({recurrenceDispatch, payload: {frequencyDay: day.value}})

  const onSelectStartDate = date => {
    let date_start = new Date(date)
    let newState = {date_start}

    if (recurrence.recurrenceType === 'none') {
      newState = {date_start, date_end: date_start}
    } else {
      if (date_end.getTime() < date_start.getTime()) {
        newState.date_end = date_start
      }
    }
    setRecurrenceValue({recurrenceDispatch, payload: newState})
  }

  const onSelectEndDate = date =>
    setRecurrenceValue({
      recurrenceDispatch,
      payload: {date_end: new Date(date)}
    })

  const onSelectRecurrenceType = type => {
    let newState = {recurrenceType: type.value}
    // Set the selected week day to the current date
    if (type.value === 'week') {
      let dayIndex = getWeekDay(new Date())
      let weekDays = [0, 0, 0, 0, 0, 0, 0]
      weekDays[dayIndex] = 1
      newState = {...newState, ...{weekDays}}
    }

    setRecurrenceValue({recurrenceDispatch, payload: newState})
  }

  const onSelectFrequency = frequency =>
    setRecurrenceValue({
      recurrenceDispatch,
      payload: {frequency: frequency.value}
    })

  const onWeekDaySelect = weekDays =>
    setRecurrenceValue({
      recurrenceDispatch,
      payload: {weekDays: weekDays.slice(0)}
    })

  const doSearch = async () => {
    await onSearch(search)
  }

  const formatTimeLabel = value => {
    const minutes = value * 60
    const d = new Date(1900, 0, 1, 0, 0, 0)
    d.setMinutes(d.getMinutes() + minutes)
    const {time, ampm} = formatTimeWithMeridiem({date: d})
    return `${time} ${ampm}`
  }

  // The time range is set by the application parameters
  const {params} = useConfigState()
  const reservationTimes = getReservationTimes({params})

  const {timeStart: dayStart, timeEnd: dayEnd} = bookingRangeByFloor[
    activeFloor.flId
  ]
    ? bookingRangeByFloor[activeFloor.flId]
    : {timeStart: null, timeEnd: null}

  const rmArrange = {dayStart, dayEnd}
  const startEndTimes = getReservationStartEndTimes({
    reservationTimes,
    rmArrange
  })

  const {start, end} = startEndTimes
  const timeRange = {min: start, max: end}

  // Room Resources
  const {resources, time_start, time_end} = search

  let seatMax = 10
  if (capacityByFloor && capacityByFloor[activeFloor.flId]) {
    seatMax = capacityByFloor[activeFloor.flId]
  }

  let timeStart = time_start
    ? convertTimeStringToNumber(time_start)
    : timeRange.min
  let timeEnd = time_end ? convertTimeStringToNumber(time_end) : timeRange.max
  const timeValue = {min: timeStart, max: timeEnd}

  const {
    date_start,
    date_end,
    recurrenceType,
    weekDays,
    frequencyDay,
    frequency
  } = recurrence

  const {TYPES, DAYS, FREQUENCY} = recurrenceLists
  const showEndDate = recurrenceType !== 'none'
  const showDaysOfWeek = recurrenceType === 'week'
  const showFrequency = recurrenceType === 'month'

  return (
    <Container>
      <Title>{_L({key: 'Search meeting spaces', localeState})}</Title>

      {showClose && (
        <CloseIcon onClick={onCloseFilter}>
          <Icons icon="CLOSE" height={14} width={14} />
        </CloseIcon>
      )}

      <FilterContainer>
        <FilterLayout>
          <TimeContainer>
            <Label>{_L({key: 'Meeting Time', localeState})}</Label>
            <TimeGroupButtonContainer>
              <ToggleButton
                value={toggleTime}
                onChange={onMeetingTimeClick}
                labels={[
                  _L({key: 'Any', localeState}),
                  _L({key: 'Select', localeState})
                ]}
                compact={false}
              />
            </TimeGroupButtonContainer>
          </TimeContainer>

          <TimeRangeContainer>
            {toggleTime && (
              <TimeSliderContainer>
                <InputRange
                  maxValue={timeRange.max}
                  minValue={timeRange.min}
                  step={0.5}
                  value={timeValue}
                  onChange={value => setMeetingTime(value)}
                  formatLabel={formatTimeLabel}
                  showSliderLabel={false}
                />
              </TimeSliderContainer>
            )}
          </TimeRangeContainer>

          <RecurrenceContainer>
            <ComponentContainer column={1}>
              <Label>{_L({key: 'Recurrence Pattern', localeState})}</Label>
              <ListSelect
                options={TYPES}
                placeholder=""
                isSearchable={false}
                value={TYPES.filter(({value}) => value === recurrenceType)}
                onChange={onSelectRecurrenceType}
              />
            </ComponentContainer>

            <ComponentContainer column={1}>
              <Label>{_L({key: 'Start Date', localeState})}</Label>
              <CalendarField
                onSelect={onSelectStartDate}
                date={date_start.toISOString()}
              />
            </ComponentContainer>

            {showEndDate && (
              <ComponentContainer column={2}>
                <Label>{_L({key: 'End Date', localeState})}</Label>
                <CalendarField
                  onSelect={onSelectEndDate}
                  date={date_end.toISOString()}
                />
              </ComponentContainer>
            )}

            {showFrequency && (
              <ComponentContainer column={1}>
                <Label>{_L({key: 'Every', localeState})}</Label>
              </ComponentContainer>
            )}
          </RecurrenceContainer>
        </FilterLayout>
        <FilterLayout>
          {showFrequency && (
            <ComponentContainer column={1} style={{marginBottom: 16}}>
              <ListSelect
                options={FREQUENCY}
                placeholder=""
                isSearchable={false}
                value={FREQUENCY.filter(({value}) => value === frequency)}
                onChange={onSelectFrequency}
              />
            </ComponentContainer>
          )}
          {showFrequency && (
            <ComponentContainer column={2}>
              <ListSelect
                options={DAYS}
                placeholder=""
                isSearchable={false}
                value={DAYS.filter(({value}) => value === frequencyDay)}
                onChange={onSelectDays}
              />
            </ComponentContainer>
          )}
        </FilterLayout>

        {showDaysOfWeek && (
          <ComponentContainer style={{paddingRight: 10}}>
            <Label>{_L({key: 'Days of Week', localeState})}</Label>
            <WeekDaySelect value={weekDays} onChange={onWeekDaySelect} />
          </ComponentContainer>
        )}
        <FilterLayout>
          <ComponentContainer column={1}>
            <Label>{_L({key: 'Seats', localeState})}</Label>
            <SeatContainer>
              <InputRange
                maxValue={seatMax}
                minValue={1}
                value={search.capacity}
                onChange={value => setCapacity(value)}
              />
            </SeatContainer>
          </ComponentContainer>
        </FilterLayout>
        <ComponentContainer>
          <Label>{_L({key: 'Amenities', localeState})}</Label>
        </ComponentContainer>
        <AmenitySelect
          selectedItems={resources}
          amenities={roomResources}
          onSelect={onSelectResources}
        />
      </FilterContainer>

      <SearchButton onClick={doSearch}>
        {isLoading ? (
          <Spinner height={26} width={26} />
        ) : (
          _L({key: 'Search', localeState})
        )}
      </SearchButton>
    </Container>
  )
}

export default MeetingRoomFilter

const Container = styled.div`
  height: 100%;
  width: 100%;
  box-sizing: border-box;
`

const FilterContainer = styled.div`
  padding: 10px 24px 24px 24px;
  height: calc(100vh - 205px);
  overflow-y: auto;
  box-sizing: border-box;
  flex: 1;
  width: 100%;

  @media (max-width: 768px) {
    height: 60vh;
  }

  @media (max-width: 420px) {
    height: calc(100vh - 114px);
  }
`

const FilterLayout = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  grid-template-columns: 1fr;
  box-sizing: border-box;

  @media (max-width: 768px) {
    grid-template-rows: 1fr;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 20px;
    grid-row-gap: 10px;
  }

  @media (max-width: 420px) {
    grid-template-rows: auto 1fr;
    grid-template-columns: 1fr;
  }
`

const TimeContainer = styled.div`
  grid-row: 1;
  grid-column: 1;
  box-sizing: border-box;
  padding-top: 20px;
`
const TimeGroupButtonContainer = styled.div`
  padding-top: 10px;
  padding-bottom: 12px;
`
const TimeRangeContainer = styled.div`
  grid-row: 2;
  grid-column: 1;
  box-sizing: border-box;

  @media (max-width: 768px) {
    grid-row: 1;
    grid-column: 2;
    padding-top: 56px;
  }

  @media (max-width: 420px) {
    grid-row: 2;
    grid-column: 1;
    padding-top: 10px;
  }
`

const RecurrenceContainer = styled.div`
  grid-row: 3;
  grid-column: 1;
  box-sizing: border-box;

  display: grid;
  grid-template-rows: auto;
  grid-template-columns: 1fr;
  grid-column-gap: 20px;

  @media (max-width: 768px) {
    grid-row: 2;
    grid-column-start: 1;
    grid-column-end: 3;
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 420px) {
    grid-row: 3;
    grid-column-end: 2;
    grid-template-columns: auto;
  }
`

const ComponentContainer = styled.div`
  box-sizing: border-box;
  grid-column: ${props => props.column};
  padding-top: ${props => props.paddingTop || '0px'};
  padding-bottom: 0;

  @media (min-width: 1023px) {
    grid-column: 1;
  }

  @media (max-width: 420px) {
    grid-column: 1;
    padding-top: 10px;
  }
`

const Label = styled.div`
  font-size: 16px;
  font-weight: normal;
  color: #a4a9b7;
  box-sizing: border-box;
  padding-top: 14px;
  padding-bottom: 10px;
`

const Title = styled.div`
  font-size: 22px;
  height: 60px;
  padding: 26px 24px 24px 24px;
  box-sizing: border-box;
  font-weight: bold;
  line-height: 26px;
`

const SearchButton = styled.button`
  height: 64px;
  width: 100%;
  background-color: #3395fe;
  color: white;
  text-align: center;
  font-size: 18px;
`

const CloseIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 38px;
  width: 38px;
  border-radius: 3px;
  background-color: #f4f5fa;
  cursor: pointer;
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 25;
`

const TimeSliderContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 6px 16px 6px;
  box-sizing: border-box;
  width: 98%;
  height: 75px;
`

const SeatContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px 6px 16px 6px;
  box-sizing: border-box;
  width: 98%;
  height: 75px;
`
